import { registerBlock } from "../registry";
import { makeId } from "../helpers";
import type { ContentBlock, ContentTag } from "../types";

function travelArticle(
  slug: string,
  opts: {
    title: string;
    teaser: string;
    readTimeMinutes: number;
    tags: ContentTag[];
    sections: {
      title: string;
      content?: string;
      bulletPoints?: string[];
    }[];
    quickSummary: { title: string; description: string }[];
    references: NonNullable<ContentBlock<"article">["body"]>["references"];
  },
): void {
  registerBlock({
    id: makeId(`moon-article-${slug}`),
    kind: "article",
    tags: ["moon", "travel", ...opts.tags],
    title: opts.title,
    subtitle: opts.teaser,
    readTimeMinutes: opts.readTimeMinutes,
    body: {
      introduction: "Travel",
      quickSummary: opts.quickSummary,
      sections: opts.sections,
      references: opts.references,
    },
  });
}

const travelReferences = [
  {
    authors: "American Society for Reproductive Medicine",
    title: "Cross-border reproductive care: an Ethics Committee opinion",
    year: 2022,
    url: "https://www.asrm.org/practice-guidance/ethics-opinions/cross-border-reproductive-care-an-ethics-committee-opinion-2022/",
    evidenceLevel: "official" as const,
  },
  {
    authors: "Human Fertilisation and Embryology Authority",
    title: "Fertility treatment abroad",
    url: "https://www.hfea.gov.uk/treatments/explore-all-treatments/fertility-treatment-abroad/",
    evidenceLevel: "official" as const,
  },
];

travelArticle("traveling-for-fertility-treatment", {
  title: "Traveling for fertility treatment",
  teaser:
    "A practical plan for coordinating a distant clinic, local monitoring, and the trips around treatment.",
  readTimeMinutes: 10,
  tags: [
    "moon-guides",
    "clinic",
    "practical",
    "pre-cycle",
    "during-cycle",
    "post-cycle",
    "ivf",
    "iui",
  ],
  quickSummary: [
    {
      title: "There are two kinds of travel patients.",
      description:
        "You may travel to a distant clinic while monitoring at home, or cross a border for some or all of your care. The plan is different for each.",
    },
    {
      title: "Map every trip before you book.",
      description:
        "Ask which visits are required, which can happen locally, and what happens if your cycle changes or a flight is delayed.",
    },
    {
      title: "Your clinic owns treatment timing.",
      description:
        "Get written instructions for monitoring, medication timing, after-hours help, and the local clinician who will act on results.",
    },
  ],
  sections: [
    {
      title: "What does travel patient mean?",
      content:
        "A travel patient may live far from their fertility clinic, use a local clinic for blood tests or ultrasounds, or travel across a border for treatment. Some people travel only for retrieval or transfer; others travel for the whole pathway. Before you commit, ask the clinic to describe the complete route in calendar terms—not just the procedure itself.",
      bulletPoints: [
        "Distant care: your main clinic directs treatment while a local provider monitors you",
        "Treatment travel: you travel for one or more time-sensitive procedures",
        "Cross-border care: your clinic, laws, records, insurance, and follow-up may span countries",
      ],
    },
    {
      title: "Choose the care model before booking travel",
      content:
        "Ask whether the clinic accepts outside monitoring and exactly what it needs from the local provider. Confirm who reviews each result, how quickly results must arrive, which time zone controls the plan, and who can change your medication. A local scan or blood draw is not useful unless the treating clinic can receive and act on it in time.",
      bulletPoints: [
        "Can monitoring happen at a local fertility clinic, hospital, or imaging center?",
        "Which lab assays and ultrasound measurements does the treating clinic require?",
        "Who sends the records, by what secure method, and by what deadline?",
        "Who is the after-hours contact if a local result changes the plan?",
      ],
    },
    {
      title: "Map every appointment and trip",
      content:
        "Build a trip map with the clinic before you pay for flights. Include the initial consultation, baseline testing, monitoring visits, trigger and retrieval, transfer, pregnancy testing, and any required follow-up. Ask how much notice you may have before a retrieval or transfer, because follicle growth and lining response can change the calendar.",
      bulletPoints: [
        "Required in-person visits and the earliest/latest acceptable arrival time",
        "Visits that can be completed locally or by telehealth",
        "A buffer for delays, canceled transfers, extra monitoring, or an additional night",
        "A plan for the route home and who will manage follow-up results",
      ],
    },
    {
      title: "Make timing and communication explicit",
      content:
        "Time zones matter most when an injection, trigger, retrieval, transfer, or lab result has a narrow window. Ask for one written schedule that labels the time zone and explains what to do if you miss a call, lose internet access, are delayed in transit, or cannot complete a local appointment. Do not convert a trigger or other time-sensitive dose on your own.",
      bulletPoints: [
        "Clinic phone number, secure messaging route, and after-hours number",
        "The time zone used for every medication and appointment time",
        "What counts as an urgent symptom and where to seek care locally",
        "Who has authority to pause, change, or cancel treatment",
      ],
    },
    {
      title: "Prepare records, language, and consent",
      content:
        "Keep a travel folder with your treatment plan, medication list using generic names, allergies, relevant results, procedure history, contact details, and insurance information. Ask whether records need translation or a specific format. For donor treatment, embryos, eggs, sperm, or a gestational carrier, ask for legal advice in both the destination country and the country where you live; the rules may not travel with the medical plan.",
      bulletPoints: [
        "Clinic and local-provider contact details, including emergency contacts",
        "Medication list, prescriptions, and a letter explaining injectable medicines",
        "Copies of test results, imaging, operative notes, and consent forms",
        "A secure way to access records without relying on one device",
      ],
    },
    {
      title: "Budget the whole journey",
      content:
        "Compare the full pathway, not only the advertised procedure price. Include medication, local monitoring, flights, lodging, meals, childcare, time off work, translation, storage, transfer or retrieval fees, and an emergency or delay fund. Ask what happens financially if the cycle is canceled, the transfer is postponed, or you need to stay longer.",
      bulletPoints: [
        "Written treatment quote with required and optional items separated",
        "Monitoring, anesthesia, laboratory, storage, and follow-up fees",
        "Change or cancellation rules for flights and accommodation",
        "Insurance exclusions for pre-existing conditions or fertility treatment",
      ],
    },
    {
      title: "If treatment is across a border",
      content:
        "Check how fertility treatment is regulated in the destination country and what standards apply to the clinic and laboratory. Ask how success rates are calculated, what multiple-pregnancy policies are used, how complaints are handled, and who will treat a complication after you return home. If donor material, embryos, or a carrier are involved, obtain qualified legal advice before treatment.",
      bulletPoints: [
        "Is there a national regulator or accreditation body you can verify?",
        "How often and at which stages must you travel?",
        "What happens to stored eggs, sperm, or embryos if you change plans?",
        "Which clinician handles urgent care and follow-up in your home country?",
        "What evidence supports any treatment add-ons being offered?",
      ],
    },
    {
      title: "Have a return-home and emergency plan",
      content:
        "Before you travel, identify where you would go locally for urgent care and how the treating clinic will communicate with that provider. Ask the clinic when travel is medically reasonable after retrieval or transfer, and what symptoms mean you should not continue the journey. Your clinic’s instructions take priority; severe pain, heavy bleeding, breathing trouble, fainting, or rapidly worsening swelling need urgent medical attention.",
      bulletPoints: [
        "Local emergency department or urgent-care location near your lodging",
        "After-hours clinic number and a copy of your treatment summary",
        "A companion who knows the medication and emergency plan",
        "Extra time and funds if travel home is not medically advisable",
      ],
    },
    {
      title: "Travel-patient checklist",
      bulletPoints: [
        "Ask the clinic for the complete calendar, time zone, and required travel windows",
        "Confirm the local monitoring provider, test requirements, and secure record handoff",
        "Get written medication, trigger, delay, and after-hours instructions",
        "Carry prescriptions, treatment records, and a clinician letter",
        "Price monitoring, medication, lodging, delays, storage, and follow-up—not only the procedure",
        "Check destination, transit, insurance, donor, storage, and cross-border legal rules",
      ],
    },
  ],
  references: travelReferences,
});

travelArticle("traveling-with-fertility-medications", {
  title: "Traveling with fertility medications",
  teaser:
    "How to plan carry-on storage, security screening, time zones, and delays without guessing about your medicines.",
  readTimeMinutes: 8,
  tags: [
    "moon-guides",
    "medications",
    "storage",
    "practical",
    "pre-cycle",
    "during-cycle",
  ],
  quickSummary: [
    {
      title: "Carry medication with you.",
      description:
        "Keep medicines and supplies in your carry-on, in their original labeled packaging, with prescriptions and a clinic letter.",
    },
    {
      title: "Storage is product-specific.",
      description:
        "Some medicines need refrigeration, some must not be frozen, and some have a defined room-temperature window. Follow the label and clinic plan.",
    },
    {
      title: "Have a delay plan.",
      description:
        "Bring enough for the trip plus a clinic-approved buffer, and know who to call before changing a dose or replacing temperature-exposed medication.",
    },
  ],
  sections: [
    {
      title: "Start with a medication travel plan",
      content:
        "Ask your fertility clinic or pharmacist for a written plan for every medicine, including its generic name, dose, timing, storage range, room-temperature limit, and what to do after a delay or temperature excursion. Fertility medicines are not interchangeable travel items: the product label and your clinic’s instructions control.",
      bulletPoints: [
        "Which medicines need refrigeration and which must stay at room temperature?",
        "How long can each medicine be out of its normal storage range?",
        "What should you do if a dose is late, a vial breaks, or a cooler fails?",
        "Which supplies are required: needles, syringes, alcohol swabs, gauze, or a sharps container?",
      ],
    },
    {
      title: "Pack medicines in your carry-on",
      content:
        "Keep medicines, needles, syringes, and temperature-control supplies with you rather than in checked baggage. Use the original labeled containers and carry prescriptions or a medication list with generic names. Pack enough for the planned journey plus a clinic-approved buffer for delays; do not mail fertility medication ahead or assume it can be replaced safely abroad.",
      bulletPoints: [
        "Original packaging with your name and the prescription label",
        "Prescription copies and a clinician letter describing injectable medicines",
        "Enough medication and supplies for delays, based on your clinic’s plan",
        "A secure, accessible pouch rather than loose items in a suitcase",
      ],
    },
    {
      title: "Plan for security screening and borders",
      content:
        "Security and import rules vary by country and may also apply during a layover. If you are flying from the United States, TSA allows medically necessary liquids and cooling accessories in carry-on baggage, but they must be screened separately. Check the rules for every airport and destination, and keep medication documentation easy to show.",
      bulletPoints: [
        "Check both the departure and destination country’s medication rules",
        "Check transit-country rules if you pass through customs during a layover",
        "Declare medically necessary liquids or supplies when required",
        "Ask the clinic or a travel-medicine specialist about restricted medicines",
      ],
    },
    {
      title: "Keep the temperature within the label’s range",
      content:
        "Do not assume that colder is safer. Some medicines must not freeze, while others have a limited room-temperature window. If refrigeration is required, ask your clinic or pharmacist what cooler, ice-pack arrangement, and temperature range are acceptable. Keep medication away from direct contact with frozen packs unless the product instructions say otherwise, and do not use a hotel freezer.",
      bulletPoints: [
        "Confirm the acceptable temperature range before leaving home",
        "Use the product’s storage instructions rather than a generic travel hack",
        "Ask what to do if medication is frozen, overheated, wet, damaged, or left in a vehicle",
        "On arrival, use a reliable refrigerator only if the product requires it",
      ],
    },
    {
      title: "Handle time zones carefully",
      content:
        "Ask the clinic to write the schedule in both your departure and destination time zones, or to explain exactly how to convert it. The clinic should direct any change to medication timing. Trigger injections and other time-sensitive doses should never be moved based on a travel app or guesswork.",
      bulletPoints: [
        "Save the clinic’s schedule offline before departure",
        "Set alarms for the clinic-approved time zone",
        "Confirm what to do if a flight crosses a time zone or loses connectivity",
        "Call the clinic before changing a dose or taking an extra dose",
      ],
    },
    {
      title: "If a delay or storage problem happens",
      content:
        "Keep the medication and packaging, note what happened and for how long, and contact the clinic or dispensing pharmacist. Do not discard or replace a dose, double a dose, or continue with a temperature-exposed medicine without advice. If you cannot reach the clinic, ask a local pharmacist or urgent-care clinician for immediate safety guidance while continuing to try the treating clinic.",
      bulletPoints: [
        "Flight delay, lost baggage, damaged vial, or broken cooler",
        "Medication left in heat, direct sun, a car, or a freezing environment",
        "Missed, late, or uncertain dose",
        "Need for replacement supplies or safe sharps disposal",
      ],
    },
    {
      title: "Arrange supplies and sharps disposal",
      content:
        "Bring the supplies your plan requires and ask the destination clinic, pharmacy, or accommodation in advance about sharps disposal. Do not leave used needles loose in a hotel bin or place them where cleaners could be injured. Local rules for carrying and disposing of sharps differ.",
      bulletPoints: [
        "Needles and syringes that match the prescribed medication",
        "Alcohol swabs, gauze, bandages, and a small disposal container",
        "A plan for replacing supplies if your stay is extended",
        "A clinic or pharmacy that can accept used sharps when possible",
      ],
    },
    {
      title: "Medication travel checklist",
      bulletPoints: [
        "Written dose, time-zone, storage, delay, and after-hours plan",
        "Original labeled medication and prescription copies",
        "Clinician letter for injectable medicines and supplies",
        "Carry-on space for medication, needles, syringes, and cooling materials",
        "Clinic-approved extra supply for travel delays",
        "Destination and transit-country rules checked before departure",
      ],
    },
  ],
  references: [
    {
      authors: "Centers for Disease Control and Prevention",
      title: "Traveling Abroad with Medicine",
      url: "https://wwwnc.cdc.gov/travel/page/travel-abroad-with-medicine",
      evidenceLevel: "official" as const,
    },
    {
      authors: "Centers for Disease Control and Prevention",
      title: "Traveling with Prohibited or Restricted Medications",
      year: 2025,
      url: "https://www.cdc.gov/yellow-book/hcp/travelers-with-additional-considerations/traveling-with-prohibited-or-restricted-medications.html",
      evidenceLevel: "official" as const,
    },
    {
      authors: "Transportation Security Administration",
      title: "TSA Travel Tips",
      url: "https://www.tsa.gov/news/press/factsheets/tsa-travel-tips",
      evidenceLevel: "official" as const,
    },
  ],
});

const crossBorderReferences = [
  {
    authors: "American Society for Reproductive Medicine",
    title: "Cross-border reproductive care: an Ethics Committee opinion",
    year: 2022,
    url: "https://www.asrm.org/practice-guidance/ethics-opinions/cross-border-reproductive-care-an-ethics-committee-opinion-2022/",
    evidenceLevel: "official" as const,
  },
  {
    authors: "Human Fertilisation and Embryology Authority",
    title: "Fertility treatment abroad",
    url: "https://www.hfea.gov.uk/treatments/explore-all-treatments/fertility-treatment-abroad/",
    evidenceLevel: "official" as const,
  },
  {
    authors: "Centers for Disease Control and Prevention",
    title: "Medical Tourism: Travel to Another Country for Medical Care",
    url: "https://wwwnc.cdc.gov/travel/page/medical-tourism",
    evidenceLevel: "official" as const,
  },
];

const storageTravelReferences = [
  {
    authors: "Human Fertilisation and Embryology Authority",
    title: "Importing and exporting sperm, eggs and embryos",
    url: "https://www.hfea.gov.uk/treatments/explore-all-treatments/importing-and-exporting-sperm-eggs-and-embryos/",
    evidenceLevel: "official" as const,
  },
  ...crossBorderReferences.slice(0, 1),
];

const donorTravelReferences = [
  {
    authors: "American Society for Reproductive Medicine",
    title: "Recommendations for practices using gestational carriers",
    year: 2022,
    url: "https://www.asrm.org/practice-guidance/practice-committee-documents/recommendations-for-practices-using-gestational-carriers-a-committee-opinion-2022/",
    evidenceLevel: "official" as const,
  },
  {
    authors: "Human Fertilisation and Embryology Authority",
    title: "Using donated eggs, sperm or embryos in treatment",
    url: "https://www.hfea.gov.uk/treatments/explore-all-treatments/using-donated-eggs-sperm-or-embryos-in-treatment/",
    evidenceLevel: "official" as const,
  },
  ...crossBorderReferences.slice(0, 1),
];

travelArticle("choosing-a-clinic-abroad", {
  title: "Choosing a fertility clinic abroad",
  teaser:
    "How to compare regulation, laboratory care, success rates, eligibility, and follow-up before you book.",
  readTimeMinutes: 9,
  tags: ["moon-guides", "clinic", "practical", "pre-cycle", "ivf", "iui"],
  quickSummary: [
    {
      title: "Check oversight first.",
      description:
        "Find out which national regulator or accreditation body covers the clinic and laboratory, and what that oversight actually means.",
    },
    {
      title: "Compare like with like.",
      description:
        "A headline pregnancy rate may not match your age, diagnosis, treatment, donor pathway, or the outcome you care about.",
    },
    {
      title: "Plan the return home.",
      description:
        "Before treatment, identify who handles complications, records, complaints, and follow-up after you leave the destination country.",
    },
  ],
  sections: [
    {
      title: "Start with regulation and laboratory oversight",
      content:
        "Ask which public regulator, licensing body, or professional accreditation covers the clinic and its laboratory. Verify the clinic through that body when possible. A clinic’s marketing language is not the same as independent oversight, and rules can differ substantially between countries.",
      bulletPoints: [
        "Who licenses or inspects the clinic and laboratory?",
        "What standards apply to storage, identification, consent, and data protection?",
        "Is the doctor or embryology laboratory named in the treatment contract?",
        "How are complaints and adverse events investigated?",
      ],
    },
    {
      title: "Understand the treatment plan before comparing prices",
      content:
        "Ask for a written plan that explains the diagnosis, proposed treatment, medication, monitoring, laboratory steps, transfer policy, and expected number of visits. A lower package price may exclude items that are medically necessary for your situation.",
      bulletPoints: [
        "Medication, blood tests, scans, anesthesia, laboratory, freezing, storage, and transfer",
        "What happens if there are no mature eggs, no blastocysts, or no transferable embryos",
        "Whether add-ons are optional and what evidence supports them",
        "Who makes treatment decisions when the response differs from the plan",
      ],
    },
    {
      title: "Read success rates with context",
      content:
        "Ask whether a figure is a pregnancy rate, live-birth rate, rate per transfer, rate per retrieval, or a cumulative result. Check the patient age, treatment type, donor status, embryo stage, reporting period, and number of cases. A rate from a different patient group cannot predict your personal outcome.",
      bulletPoints: [
        "Same age band and treatment type",
        "Own eggs versus donor eggs or sperm",
        "Fresh versus frozen transfer and single versus multiple embryo transfer",
        "Whether the clinic reports all cycles or only selected outcomes",
      ],
    },
    {
      title: "Check eligibility and legal fit",
      content:
        "Confirm that the destination country and clinic can legally treat you and provide the treatment you need. Rules may differ for single patients, same-sex couples, donor treatment, PGT, embryo testing, surrogacy, or the number of embryos transferred. Get qualified legal advice when the legal status of treatment, donors, embryos, or a future child could cross borders.",
      bulletPoints: [
        "Patient eligibility and required documents",
        "Donor identity, screening, consent, and future information rights",
        "Parentage, citizenship, birth registration, or carrier arrangements",
        "Storage, export, import, and disposal rules",
      ],
    },
    {
      title: "Ask who will care for you after you leave",
      content:
        "A destination clinic should be able to explain its after-hours contact, emergency pathway, records process, and follow-up schedule. Ask who will treat a complication at home and how that clinician will receive the treatment summary. Do not assume your local healthcare system or insurer will automatically coordinate with an overseas clinic.",
      bulletPoints: [
        "Named after-hours contact and urgent-care instructions",
        "Complete treatment summary and copies of laboratory reports before returning",
        "Who interprets the pregnancy test and later results",
        "How complaints, refunds, or disputes are handled across borders",
      ],
    },
    {
      title: "Clinic comparison checklist",
      bulletPoints: [
        "Verify the clinic and laboratory’s regulator or accreditation",
        "Request the complete treatment plan and costed quote",
        "Ask how success rates are defined and whether they match your situation",
        "Confirm eligibility, consent, donor, storage, and parentage rules",
        "Map required visits and local monitoring before booking travel",
        "Get the emergency, records, and complaints pathway in writing",
      ],
    },
  ],
  references: crossBorderReferences,
});

travelArticle("remote-monitoring-for-fertility-treatment", {
  title: "Remote monitoring for fertility treatment",
  teaser:
    "How to coordinate a treating clinic with local blood tests, scans, records, and time-sensitive decisions.",
  readTimeMinutes: 8,
  tags: [
    "moon-guides",
    "clinic",
    "clinic-visit",
    "practical",
    "pre-cycle",
    "during-cycle",
  ],
  quickSummary: [
    {
      title: "Local monitoring needs a handoff plan.",
      description:
        "The local provider, treating clinic, and patient need to know who orders, performs, sends, and interprets each result.",
    },
    {
      title: "Tests must be compatible.",
      description:
        "Ask the treating clinic which lab assays, ultrasound measurements, reporting formats, and turnaround times it accepts.",
    },
    {
      title: "The treating clinic still directs care.",
      description:
        "Local monitoring can collect information, but medication changes and time-sensitive decisions should come from the treating team.",
    },
  ],
  sections: [
    {
      title: "What remote monitoring can look like",
      content:
        "Remote monitoring usually means that a clinic near you performs some blood tests or ultrasounds while a different fertility clinic directs treatment. It may reduce travel, but it adds coordination work. Confirm that both providers agree to the arrangement before the cycle starts.",
      bulletPoints: [
        "Local ultrasound and blood testing with same-day secure transmission",
        "Telehealth appointments with the treating clinic",
        "A local provider who can accommodate short-notice monitoring",
        "Travel to the main clinic only for procedures that cannot happen locally",
      ],
    },
    {
      title: "Choose the local provider with the treating clinic",
      content:
        "Ask the treating clinic whether it has preferred local partners or specific requirements. A general imaging center or laboratory may not offer the timing, measurements, or reproductive-health context the treatment plan needs. Confirm availability on weekends and around holidays.",
      bulletPoints: [
        "Can the provider offer the required scan and blood tests on short notice?",
        "Does it understand fertility monitoring and the requested measurements?",
        "Can it send results securely and speak with the treating clinic if needed?",
        "Who covers the service if the provider is closed or the cycle changes?",
      ],
    },
    {
      title: "Agree on the result handoff",
      content:
        "Before your first appointment, agree on who receives the result, what must be included, how it is labeled, and when it is due. Keep your own copy, but do not rely on forwarding a screenshot from a patient portal if the treating clinic needs an official report.",
      bulletPoints: [
        "Patient identifiers and date/time of the sample or scan",
        "Units, reference ranges, follicle measurements, and endometrial measurements",
        "Secure transmission route and a named recipient",
        "Confirmation that the treating clinic received and reviewed the result",
      ],
    },
    {
      title: "Make the time zone and decision chain clear",
      content:
        "Ask which time zone controls appointment and medication times, how quickly the treating clinic will respond, and who can be reached after hours. The local provider should not independently change a fertility medication plan unless the treating clinic has explicitly arranged that responsibility.",
      bulletPoints: [
        "Written calendar with the time zone on every time-sensitive instruction",
        "Expected turnaround for results and medication decisions",
        "After-hours number for a result that cannot wait",
        "Plan for lost internet, delayed flights, or a closed local provider",
      ],
    },
    {
      title: "Have a backup if remote monitoring breaks down",
      content:
        "Ask what happens if a local appointment is unavailable, the report is delayed, the measurements are incomplete, or the cycle progresses faster than expected. A backup may be another local provider, a trip to the treating clinic, a revised calendar, or a canceled cycle. The clinic should explain the options before treatment begins.",
      bulletPoints: [
        "Second local provider or hospital contact",
        "Travel window for an unplanned visit",
        "Extra lodging or transportation budget",
        "Clear instructions for a missed scan, missed blood test, or late result",
      ],
    },
    {
      title: "Remote-monitoring checklist",
      bulletPoints: [
        "Treating clinic has approved the local provider",
        "Test methods, measurements, units, and turnaround times are written down",
        "Secure result transmission and confirmation process are agreed",
        "Time zone, medication authority, and after-hours contacts are clear",
        "Backup provider and travel plan exist before the cycle starts",
      ],
    },
  ],
  references: crossBorderReferences.slice(0, 2),
});

travelArticle("planning-your-fertility-treatment-trip", {
  title: "Planning your fertility treatment trip",
  teaser:
    "A trip-by-trip planning guide for flights, lodging, companions, documents, monitoring, and recovery time.",
  readTimeMinutes: 8,
  tags: [
    "moon-guides",
    "practical",
    "pre-cycle",
    "during-cycle",
    "post-cycle",
    "cost",
    "ivf",
    "iui",
  ],
  quickSummary: [
    {
      title: "Plan around windows, not one date.",
      description:
        "Fertility treatment calendars can move. Build buffers around monitoring, retrieval, transfer, and the trip home.",
    },
    {
      title: "Make the stay workable.",
      description:
        "Lodging, transport, meals, childcare, companion support, and recovery space can matter as much as the flight.",
    },
    {
      title: "Keep a return-home plan.",
      description:
        "Leave with records, prescriptions, after-hours contacts, and a clear plan for follow-up in your home area.",
    },
  ],
  sections: [
    {
      title: "Build a trip map with the clinic",
      content:
        "Ask the clinic to separate the pathway into trips and possible trips: consultation, baseline, monitoring, retrieval, transfer, pregnancy testing, and follow-up. Some visits may happen locally, while others require you to be near the clinic on short notice. Do not book nonrefundable travel before you understand the windows.",
      bulletPoints: [
        "Earliest arrival, latest safe departure, and possible extra nights",
        "Visits that can happen locally or by telehealth",
        "Expected notice before retrieval or transfer",
        "What happens if the cycle is canceled or the transfer is postponed",
      ],
    },
    {
      title: "Book for flexibility",
      content:
        "When possible, choose changeable flights and accommodation close to the clinic or reliable transport. Ask the clinic how often patients need to extend a stay and whether weekends or holidays affect monitoring and laboratory timing.",
      bulletPoints: [
        "Change and cancellation terms for flights, lodging, and rental cars",
        "A reserve for an extra night or a new return flight",
        "Travel time to the clinic at the appointment hour",
        "A plan if weather, strikes, or illness disrupt the route",
      ],
    },
    {
      title: "Choose lodging that supports treatment",
      content:
        "Look for a quiet place with reliable transport, a suitable refrigerator only when your medication needs one, space to recover, and access to food and urgent care. Ask the clinic whether you need a companion for sedation or after a procedure.",
      bulletPoints: [
        "Safe route to the clinic and local emergency care",
        "A companion who can drive, translate, or stay with you if needed",
        "Kitchen, meals, hydration, and recovery space",
        "Childcare, work, or accessibility arrangements",
      ],
    },
    {
      title: "Pack the clinical paperwork",
      content:
        "Carry your treatment calendar, medication list, prescriptions, allergies, recent results, clinic contacts, insurance information, and a clinician letter for injectable medicines. Keep copies securely online and offline. Ask for the treatment summary and laboratory records before you return home.",
      bulletPoints: [
        "Original medication packaging and prescription copies",
        "Results, imaging, procedure notes, and consent forms",
        "Destination and home-clinic contact information",
        "Translation of essential medical information when needed",
      ],
    },
    {
      title: "Budget the time around the procedure",
      content:
        "The procedure is only one part of the trip. Include monitoring days, medication teaching, recovery time, waiting for results, and the possibility of a changed plan. Ask whether the clinic recommends a specific amount of time before travel home.",
      bulletPoints: [
        "Time off work and companion leave",
        "Childcare, pet care, meals, and local transport",
        "Recovery days and follow-up appointments",
        "A financial buffer for delays or an extended stay",
      ],
    },
    {
      title: "Trip-planning checklist",
      bulletPoints: [
        "Get the clinic’s travel windows and possible-change plan",
        "Book flexible transport and lodging near care",
        "Confirm companion, childcare, accessibility, and recovery needs",
        "Pack records, prescriptions, medication, supplies, and contacts",
        "Budget monitoring, meals, lodging, delays, and follow-up",
        "Plan how results and records will reach your home team",
      ],
    },
  ],
  references: crossBorderReferences.slice(0, 3),
});

travelArticle("when-travel-plans-change-during-treatment", {
  title: "When travel plans change during treatment",
  teaser:
    "What to ask about delays, faster-than-expected response, canceled transfers, missed doses, and extra nights.",
  readTimeMinutes: 7,
  tags: [
    "moon-guides",
    "practical",
    "during-cycle",
    "post-cycle",
    "medications",
    "clinic",
  ],
  quickSummary: [
    {
      title: "Changes are part of the planning conversation.",
      description:
        "Ask in advance how the clinic handles delays, extra monitoring, cancellation, or a transfer that moves to another day.",
    },
    {
      title: "Call before changing medication.",
      description:
        "A flight delay or missed dose is a reason to contact the treating team, not to improvise a new schedule.",
    },
    {
      title: "Keep a practical buffer.",
      description:
        "Extra lodging, transport, supplies, and an after-hours contact can turn a stressful change into a manageable one.",
    },
  ],
  sections: [
    {
      title: "Ask for the change plan before you travel",
      content:
        "At the start of treatment, ask what types of changes are common and who will contact you. The clinic should explain what happens if your follicles or lining develop faster or slower than expected, if a result arrives late, or if a procedure cannot happen on the planned day.",
      bulletPoints: [
        "Who calls or messages you when the calendar changes",
        "How much notice you may have",
        "Which decisions require the treating clinician",
        "Which costs are added if the stay or monitoring expands",
      ],
    },
    {
      title: "If a flight or train is delayed",
      content:
        "Contact the treating clinic as soon as you know the delay and give your exact location, medication schedule, and new arrival time. Ask whether you should continue, change, or hold a dose, and where to seek care if you develop symptoms during transit. Do not double a dose to compensate for a delay.",
      bulletPoints: [
        "Keep the clinic’s after-hours number offline",
        "Share the new itinerary and time zone",
        "Keep medication and records with you",
        "Ask for written instructions if the plan changes",
      ],
    },
    {
      title: "If the cycle moves faster or slower",
      content:
        "A monitoring result may lead to another local visit, a new travel date, a dose change, a trigger time, or a canceled cycle. Ask the clinic to state the new plan in the time zone you are using and to identify the next decision point.",
      bulletPoints: [
        "Next scan or blood test and its deadline",
        "Medication change and the responsible clinician",
        "New retrieval or transfer window",
        "Whether lodging, work, or companion plans need to change",
      ],
    },
    {
      title: "If a transfer is postponed or canceled",
      content:
        "Ask what happens to the embryo, medication, storage, and follow-up plan. A postponed transfer may change the length of your stay or allow you to return home, but the treating clinic should decide the medical schedule and explain any new monitoring or medication instructions.",
      bulletPoints: [
        "Whether the embryo will remain stored and under whose consent",
        "Whether medication should continue, stop, or change",
        "When the next transfer or review could occur",
        "How fees, flights, lodging, and time off are handled",
      ],
    },
    {
      title: "If you need to stay longer",
      content:
        "Keep a short list of flexible lodging, transport, pharmacy, and local-care options. Ask the clinic for a written treatment summary if you need another provider, and keep enough medication and supplies only as directed by the clinic.",
      bulletPoints: [
        "Extended lodging and accessible transport",
        "Medication and supply replacement through a safe, licensed route",
        "Local urgent care or emergency department",
        "Updated work, childcare, and companion arrangements",
      ],
    },
    {
      title: "Change-of-plan checklist",
      bulletPoints: [
        "Contact the treating clinic before changing a dose or itinerary",
        "Write down the new time zone, schedule, and next decision point",
        "Ask about medical, lodging, transport, and financial consequences",
        "Keep records of calls, instructions, receipts, and new appointments",
        "Know where to seek urgent care if symptoms develop during travel",
      ],
    },
  ],
  references: [...travelReferences, ...crossBorderReferences.slice(2)],
});

travelArticle("traveling-after-retrieval-or-embryo-transfer", {
  title: "Traveling after retrieval or embryo transfer",
  teaser:
    "How to make a clinic-specific recovery and return-home plan after a time-sensitive fertility procedure.",
  readTimeMinutes: 7,
  tags: ["moon-guides", "practical", "recovery", "post-cycle", "ivf", "fet"],
  quickSummary: [
    {
      title: "There is no universal travel clearance.",
      description:
        "Your clinic should tell you when travel is reasonable based on the procedure, your symptoms, and your medical history.",
    },
    {
      title: "Recovery planning matters.",
      description:
        "Plan transport, hydration, food, rest, medication, and a companion rather than treating the procedure as a quick stop.",
    },
    {
      title: "Know the urgent symptoms.",
      description:
        "Leave with written instructions for who to call and where to seek care if symptoms worsen during the trip home.",
    },
  ],
  sections: [
    {
      title: "Ask the clinic when travel is reasonable",
      content:
        "The safest timing after retrieval or embryo transfer depends on the procedure, sedation, your response, complications, and the clinic’s protocol. Ask for a written recommendation that considers the trip length, transport type, destination, and access to care. Do not use another patient’s travel timeline as your clearance.",
      bulletPoints: [
        "When you may leave the clinic and the destination area",
        "Whether you need a companion after sedation or a procedure",
        "Which activities, lifting, exercise, or travel conditions to avoid",
        "Whether a local clinician should review you before the journey",
      ],
    },
    {
      title: "Plan the route home around recovery",
      content:
        "Ask how to travel comfortably and what support you may need. A direct trip with access to water, food, medication, and a companion may be easier than a long connection, but your clinic should advise you based on your situation. Arrange a way to stop or seek care if you feel unwell.",
      bulletPoints: [
        "Direct route or flexible travel where possible",
        "A companion who knows the clinic’s instructions",
        "Medication, records, water, food, and comfortable clothing",
        "Local emergency-care location along the route or near home",
      ],
    },
    {
      title: "After retrieval",
      content:
        "Retrieval involves a procedure and may involve sedation, cramping, bleeding, bloating, or a risk of complications such as OHSS. Ask the clinic what symptoms are expected, what should prompt a call, and what requires urgent care. If you are not recovering as expected, contact the treating clinic before continuing the journey.",
      bulletPoints: [
        "Sedation or anesthesia restrictions and companion requirements",
        "Expected pain, bleeding, bloating, and medication instructions",
        "When reduced urination, breathing trouble, severe pain, or rapid swelling needs urgent care",
        "Who can assess you if you are no longer near the treating clinic",
      ],
    },
    {
      title: "After embryo transfer",
      content:
        "Ask the clinic which medicines to continue, when to test, and what follow-up will happen at home. Normal movement or a return journey does not replace the clinic’s individual instructions, and symptoms that concern you should be reported rather than explained away as travel effects.",
      bulletPoints: [
        "Medication schedule and the time zone for each dose",
        "Pregnancy-test date and how results reach the clinic",
        "What bleeding, pain, fever, or other symptoms should prompt a call",
        "Who manages urgent care and follow-up after you return",
      ],
    },
    {
      title: "Recovery and return-home checklist",
      bulletPoints: [
        "Get written travel and activity guidance from the treating clinic",
        "Arrange a companion when sedation, symptoms, or distance make it appropriate",
        "Keep medication, records, emergency numbers, and supplies accessible",
        "Know the urgent-care route near your lodging and home",
        "Contact the clinic if symptoms worsen or recovery differs from expectations",
      ],
    },
  ],
  references: [
    ...crossBorderReferences.slice(0, 2),
    {
      authors: "Centers for Disease Control and Prevention",
      title: "Medical Tourism: Travel to Another Country for Medical Care",
      url: "https://wwwnc.cdc.gov/travel/page/medical-tourism",
      evidenceLevel: "official" as const,
    },
  ],
});

travelArticle("fertility-treatment-travel-costs-and-insurance", {
  title: "Travel costs and insurance for fertility treatment",
  teaser:
    "How to price the whole pathway, check exclusions, and build a buffer for delays or complications.",
  readTimeMinutes: 8,
  tags: ["moon-guides", "cost", "insurance", "practical", "pre-cycle"],
  quickSummary: [
    {
      title: "The package price is not the trip price.",
      description:
        "Add medication, monitoring, flights, lodging, meals, childcare, storage, and follow-up to the clinic’s quote.",
    },
    {
      title: "Ask what insurance excludes.",
      description:
        "Fertility treatment, pre-existing conditions, complications, cancellation, and medical evacuation may be treated differently.",
    },
    {
      title: "Build a change fund.",
      description:
        "A flexible reserve can cover an extra monitoring visit, additional nights, a postponed transfer, or an unexpected medical need.",
    },
  ],
  sections: [
    {
      title: "Request the complete costed plan",
      content:
        "Ask the clinic to separate required treatment from optional add-ons and to list what is not included. Compare quotes using the same treatment, medication, monitoring, laboratory, storage, and follow-up assumptions.",
      bulletPoints: [
        "Consultation, testing, medication, monitoring, anesthesia, retrieval, transfer, and laboratory fees",
        "Embryo or gamete freezing, storage, PGT, donor material, and shipping",
        "Local monitoring and translation costs",
        "Cancellation, refund, and postponed-treatment rules",
      ],
    },
    {
      title: "Price the travel around treatment",
      content:
        "Add every day you may need to be away, including monitoring, waiting for results, recovery, and a possible extra night. Include a companion’s travel and time off when one is needed.",
      bulletPoints: [
        "Flights, trains, local transport, parking, and airport transfers",
        "Lodging, meals, accessibility, and a suitable refrigerator when required",
        "Work leave, childcare, pet care, and companion costs",
        "A reserve for delays, a changed return date, or a canceled procedure",
      ],
    },
    {
      title: "Check medical and travel insurance separately",
      content:
        "Ask both the clinic and insurer what is covered. Travel policies may exclude pre-existing conditions, planned treatment, complications from fertility care, or care outside a network. If you travel abroad for medical care, ask whether the policy includes emergency treatment, repatriation, and medical evacuation.",
      bulletPoints: [
        "Treatment abroad and complications related to planned care",
        "Pre-existing conditions and recent procedures",
        "Trip cancellation or interruption",
        "Emergency care, repatriation, and medical evacuation",
      ],
    },
    {
      title: "Ask how a changed plan affects the bill",
      content:
        "Before paying, ask what you owe if monitoring increases, the cycle is canceled, a retrieval yields no usable eggs, or a transfer moves to another cycle. Get the answer in writing and keep receipts for travel and medical expenses.",
      bulletPoints: [
        "Extra scans, blood tests, medication, nights, or travel",
        "Canceled, postponed, or converted cycles",
        "Follow-up care in the destination or home country",
        "Storage and future-use fees",
      ],
    },
    {
      title: "Keep financial and medical records together",
      content:
        "Save the quote, invoices, prescriptions, receipts, treatment summary, and insurance correspondence. Keep copies accessible if you need to make a claim or explain care to a home clinician.",
      bulletPoints: [
        "Clinic contract and itemized quote",
        "Receipts for treatment, medication, transport, and lodging",
        "Insurance policy number and 24/7 assistance contact",
        "Treatment records and emergency documentation",
      ],
    },
    {
      title: "Travel-cost checklist",
      bulletPoints: [
        "Get a written quote with required and optional care separated",
        "Add monitoring, medication, transport, lodging, meals, and time off",
        "Check treatment, complication, cancellation, and evacuation exclusions",
        "Ask what happens financially if the cycle or travel dates change",
        "Create a contingency fund and keep every receipt",
      ],
    },
  ],
  references: crossBorderReferences,
});

travelArticle("moving-frozen-eggs-sperm-or-embryos-across-borders", {
  title: "Moving frozen eggs, sperm, or embryos across borders",
  teaser:
    "Why cryostorage transport is clinic-led, regulated, and different from carrying medication in your luggage.",
  readTimeMinutes: 8,
  tags: [
    "moon-guides",
    "storage",
    "clinic",
    "practical",
    "pre-cycle",
    "egg-freezing",
    "embryo-freezing",
    "sperm",
  ],
  quickSummary: [
    {
      title: "Do not hand-carry cryostorage material.",
      description:
        "Eggs, sperm, and embryos should be moved through the sending and receiving clinics using a specialist biological-materials process.",
    },
    {
      title: "Approval and consent may be required.",
      description:
        "The rules depend on the countries, clinics, donor status, storage consent, and material being moved.",
    },
    {
      title: "Start early.",
      description:
        "Clinic checks, paperwork, courier planning, and regulatory approvals can take longer than a normal trip.",
    },
  ],
  sections: [
    {
      title: "This is a clinic-to-clinic transfer",
      content:
        "Frozen eggs, sperm, and embryos are biological materials, not ordinary luggage. Start with the sending clinic and the receiving clinic. They should confirm whether they can work together, which approvals apply, and which specialist courier will maintain the required conditions during transport.",
      bulletPoints: [
        "Sending clinic has identified and released the material under valid consent",
        "Receiving clinic has confirmed it can accept, store, and use the material",
        "Both clinics understand the destination country’s requirements",
        "A specialist biological-materials courier is arranged by the clinics",
      ],
    },
    {
      title: "Confirm identity, chain of custody, and storage",
      content:
        "Ask how the clinics verify the identity and number of samples, document every handoff, protect the container, and record the condition on arrival. Ask where the material will be stored, how long consent lasts, and what happens if the shipment is delayed or cannot be accepted.",
      bulletPoints: [
        "Unique identifiers and labeling at both clinics",
        "Documented handoffs and courier tracking",
        "Transport container designed for cryopreserved material",
        "Receiving inspection and notification when the material arrives",
      ],
    },
    {
      title: "Check consent and donor rules",
      content:
        "Consent to storage, transport, treatment, donation, and future use may be separate. Donor material can carry additional consent, screening, identity, and storage requirements. Do not assume consent valid in one country is enough for another.",
      bulletPoints: [
        "Consent from the relevant egg, sperm, embryo, or donor providers",
        "Storage renewal and expiry dates",
        "Permission for export, import, treatment, donation, or research",
        "Donor information and future identity or disclosure rules",
      ],
    },
    {
      title: "Ask about timeline and cost",
      content:
        "Request a timeline that includes clinic review, paperwork, regulatory approval, courier booking, customs or border requirements, and receiving-clinic acceptance. Ask for the total cost, including clinic fees, courier fees, storage, translation, and any application or approval fees.",
      bulletPoints: [
        "Earliest realistic shipping date",
        "What can delay or prevent the transfer",
        "Insurance or liability if the material is damaged or rejected",
        "Who pays for storage or a second shipment",
      ],
    },
    {
      title: "Cross-border cryostorage checklist",
      bulletPoints: [
        "Contact both clinics before making travel or treatment bookings",
        "Confirm country, clinic, donor, consent, and storage requirements",
        "Use a specialist courier arranged through the clinics",
        "Get the chain-of-custody and receiving confirmation process in writing",
        "Budget approvals, courier, translation, storage, and possible delays",
        "Keep copies of all consent, shipping, and storage records",
      ],
    },
  ],
  references: storageTravelReferences,
});

travelArticle("donor-and-carrier-treatment-across-borders", {
  title: "Donor and carrier treatment across borders",
  teaser:
    "Questions about donor consent, carrier arrangements, screening, parentage, and follow-up when care spans countries.",
  readTimeMinutes: 9,
  tags: [
    "moon-guides",
    "clinic",
    "practical",
    "emotional",
    "pre-cycle",
    "ivf",
    "both-partners",
  ],
  quickSummary: [
    {
      title: "Legal advice is part of the care plan.",
      description:
        "The law may differ between the destination country and home, especially for donors, carriers, consent, and parentage.",
    },
    {
      title: "Ask how everyone is protected.",
      description:
        "Screening, counseling, consent, medical care, compensation, and emergency plans should be clear for donors, carriers, and intended parents.",
    },
    {
      title: "Plan for the child’s return home.",
      description:
        "Parentage, citizenship, birth registration, travel documents, and medical records may require separate advice and preparation.",
    },
  ],
  sections: [
    {
      title: "Start with independent legal advice",
      content:
        "When donor treatment or a gestational carrier crosses borders, consult qualified lawyers who understand both the destination country and your home country. A clinic can explain its medical process, but it may not be responsible for your parentage, citizenship, immigration, or contract position after you leave.",
      bulletPoints: [
        "Who is recognized as a legal parent and when",
        "Whether donor or carrier agreements are enforceable",
        "Birth registration, citizenship, passports, and travel home",
        "What happens if treatment, birth, or return travel is delayed",
      ],
    },
    {
      title: "Ask about donor identity and consent",
      content:
        "Donation rules vary by country. Ask how donors are recruited and screened, what information is recorded, whether donation is anonymous or identity-release, how consent can change, and how donor material can be stored or moved. Consider how the rules affect the future child, not only the treatment cycle.",
      bulletPoints: [
        "Infectious-disease and genetic screening",
        "Donor medical and family-history information",
        "Identity, disclosure, sibling, and contact rules",
        "Consent to storage, export, future use, or donation",
      ],
    },
    {
      title: "Understand carrier screening and support",
      content:
        "A carrier arrangement should include medical screening, psychological counseling, informed consent, independent representation, and a clear plan for pregnancy care and complications. Ask who coordinates care if the carrier and intended parents live in different places.",
      bulletPoints: [
        "Medical and psychological screening for the carrier",
        "Independent legal advice and counseling for every party",
        "Prenatal care, emergency care, and delivery location",
        "Plan for multiple pregnancy, loss, or an unexpected medical outcome",
      ],
    },
    {
      title: "Plan records, communication, and emergencies",
      content:
        "Make sure the clinic, donor or carrier team, intended parents, home clinicians, and legal advisers know who can access which records. Identify the emergency contact and the clinician responsible at every stage. Keep medical records and consent documents available in the languages needed by the people providing care.",
      bulletPoints: [
        "Shared contact list and after-hours plan",
        "Secure medical-record access and translation",
        "Travel and accommodation plan for appointments or complications",
        "Mental-health support for donors, carriers, intended parents, and partners",
      ],
    },
    {
      title: "Third-party reproduction travel checklist",
      bulletPoints: [
        "Obtain independent legal advice in both relevant countries",
        "Confirm donor or carrier screening, counseling, consent, and compensation rules",
        "Ask about identity, disclosure, storage, export, and future-use rights",
        "Plan parentage, citizenship, birth registration, and travel documents",
        "Create medical, communication, emergency, and mental-health support plans",
      ],
    },
  ],
  references: donorTravelReferences,
});
